import type {
  HighlightConfig,
  SuperLatexAnnotation,
  SuperLatexConnection,
  SuperLatexContent,
  SuperLatexLayout,
  SuperLatexNode,
} from '@examdojo/super-latex';

import { v4 as uuid } from 'uuid';
import { FORMULA_CALLOUT_NODE_NAME } from '../../editor/extensions/formula-callout';
import { ContentBlockType, FormulaCalloutBlock } from '../../model';
import { BlockToTiptapConverter, convertBlocksToTiptapNodes, ConverterContext } from '../blocks-to-tiptap.converter';
import { convertTiptapNodesToBlocks, TiptapToBlockConverter } from '../tiptap-to-blocks.converter';

export const convertFormulaCalloutBlockToTiptap = ((block, context?: ConverterContext) => {
  const formula: SuperLatexContent = {
    latex_nodes: block.formula?.latex_nodes ?? [],
    ...(block.formula?.annotations && { annotations: block.formula.annotations }),
    ...(block.formula?.connections && { connections: block.formula.connections }),
    ...(block.formula?.layout && { layout: block.formula.layout }),
    ...(block.formula?.highlighted_anchors && { highlighted_anchors: block.formula.highlighted_anchors }),
  };

  return {
    type: FORMULA_CALLOUT_NODE_NAME,
    attrs: {
      id: block.id,
      formula,
      explanation: block.explanation ?? '',
      show_derivation: block.show_derivation ?? false,
      center_formula: block.center_formula ?? true,
      formula_booklet: block.formula_booklet ?? false,
      formula_booklet_url: block.formula_booklet_url ?? '',
      borderless: block.borderless ?? false,
      ...(block.custom_css && { custom_css: block.custom_css }),
      ...(block.custom_style && { custom_style: block.custom_style }),
    },
    content: block.content?.length ? convertBlocksToTiptapNodes(block.content, context) : [{ type: 'paragraph' }],
  };
}) satisfies BlockToTiptapConverter<FormulaCalloutBlock>;

export const convertTiptapToFormulaCalloutBlock = ((json, context?) => {
  const attrs = json.attrs ?? {};
  const content = json.content ?? [];

  const formula = (attrs['formula'] as SuperLatexContent) ?? { latex_nodes: [] };

  const formulaContent: SuperLatexContent = {
    latex_nodes: (formula.latex_nodes as SuperLatexNode[]) ?? [],
    ...(formula.annotations && { annotations: formula.annotations as SuperLatexAnnotation[] }),
    ...(formula.connections && { connections: formula.connections as SuperLatexConnection[] }),
    ...(formula.layout && { layout: formula.layout as SuperLatexLayout }),
    ...(formula.highlighted_anchors && {
      highlighted_anchors: formula.highlighted_anchors as Record<string, HighlightConfig>,
    }),
  };

  return {
    id: (attrs['id'] as string) ?? uuid(),
    type: ContentBlockType.FormulaCallout,
    formula: formulaContent,
    explanation: (attrs['explanation'] as string) ?? '',
    ...(attrs['show_derivation'] && { show_derivation: attrs['show_derivation'] as boolean }),
    ...(attrs['center_formula'] === false && { center_formula: false }),
    ...(attrs['formula_booklet'] && { formula_booklet: true }),
    ...(attrs['formula_booklet_url'] && { formula_booklet_url: attrs['formula_booklet_url'] as string }),
    ...(attrs['borderless'] && { borderless: true }),
    ...(attrs['custom_css'] && { custom_css: attrs['custom_css'] as string }),
    ...(attrs['custom_style'] && { custom_style: attrs['custom_style'] as Record<string, string> }),
    content: content.length ? convertTiptapNodesToBlocks(content, context) : [],
  };
}) satisfies TiptapToBlockConverter;
